<script lang="ts">
import type { Component } from 'vue'

export interface FormFieldProps {
  as?: string | Component
  name?: string
  label?: string
  help?: string
  errorMessage?: string
  required?: boolean
  disabled?: boolean
  class?: string | object
}

export interface FormFieldSlots {
  label?: (props: { label?: string }) => any
  help?: (props: { help?: string }) => any
  error?: (props: { errorMessage?: string }) => any
  default?: (props: { errorMessage?: string }) => any
}
const ui = {
  root: 'flex flex-col gap-2',
  wrapper: '',
  labelWrapper: 'flex content-center items-center justify-between',
  label: 'block w-full text-[14px] font-600 leading-[18px] text-secondary-900',
  container: 'relative',
  error: 'mt-1.25 text-md text-critical-600',
  help: 'mt-1.25 text-md text-secondary-800',
  required: {
    label: `after:content-['*'] after:ms-0.5 after:text-red-600`,
  },
}
</script>

<script setup lang="ts">
import { Label, Primitive } from 'reka-ui'
import { computed, ref, useId } from 'vue'

const props = defineProps<FormFieldProps>()
const slots = defineSlots<FormFieldSlots>()

const id = ref(useId())

const rootClass = computed(() => [
  ui.root,
  props.class,
  props.disabled && 'opacity-30',
])
</script>

<template>
  <Primitive :as="as || 'div'" :class="rootClass">
    <div :class="ui.wrapper">
      <div v-if="label || !!slots.label" :class="ui.labelWrapper">
        <Label :for="id" :class="[ui.label, required && ui.required.label]">
          <slot name="label" :label>
            {{ label }}
          </slot>
        </Label>
      </div>
    </div>

    <div :class="[(label || !!slots.label) && ui.container]">
      <slot :error-message />

      <div v-if="errorMessage || !!slots.error" :id="`${id}-error`" :class="ui.error">
        <slot name="error" :error-message>
          {{ errorMessage }}
        </slot>
      </div>
      <div v-if="help || !!slots.help" :class="ui.help">
        <slot name="help" :help>
          {{ help }}
        </slot>
      </div>
    </div>
  </Primitive>
</template>
